import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useOutletContext } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState, useUrlSearch, SearchInput, matchText } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Plus, Pencil, Trash2, Upload, Download, CalendarPlus, Eye } from "lucide-react";
import ImportCustomersDialog from "./ImportCustomersDialog";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import ClientIssuesDialog, { ClientIssuesBanner } from "@/components/ClientIssuesDialog";

const empty = { name: "", email: "", phone: "", address: "", suburb: "", postcode: "", notes: "", preferred_channel: "sms", sms_opt_out: false, business_name: "", abn: "" };

export default function Customers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useUrlSearch();
  const { alerts = {}, refreshAlerts } = useOutletContext() || {};
  const issues = alerts.clients?.items || [];
  const [issuesOpen, setIssuesOpen] = useState(false);
  const issueFor = (id) => issues.find((i) => i.id === id);

  const filtered = items.filter((c) =>
    matchText(search, c.name, c.email, c.phone, c.suburb, c.address));

  const load = () => api.get("/customers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!params.get("new")) return;
    setForm(empty); setEditing(null); setOpen(true);
    params.delete("new");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditing(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const payload = {
      name: form.name, email: form.email, phone: form.phone,
      address: form.address, suburb: form.suburb, postcode: form.postcode, notes: form.notes,
      preferred_channel: form.preferred_channel || "sms",
      sms_opt_out: !!form.sms_opt_out,
      business_name: form.business_name || "", abn: form.abn || "",
    };
    try {
      if (editing) await api.put(`/customers/${editing}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false);
      load();
      refreshAlerts?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Client deleted");
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const exportCsv = async () => {
    try {
      const res = await api.get("/customers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sterling-customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Client Directory"
        title="Clients"
        description="Every home and the details that make each clean personal."
        action={
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={exportCsv} data-testid="export-customers-button" className="rounded-sm gap-2" disabled={items.length === 0}>
              <Download className="w-4 h-4" /> Export CSV
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-customers-button" className="rounded-sm gap-2">
              <Upload className="w-4 h-4" /> Import CSV
            </Button>
            <Button onClick={openNew} data-testid="add-customer-button" className="rounded-sm gap-2">
              <Plus className="w-4 h-4" /> New Client
            </Button>
          </div>
        }
      />

      <ClientIssuesBanner items={issues} onReview={() => setIssuesOpen(true)} />

      {items.length > 0 && (
        <div className="mb-6">
          <SearchInput value={search} onChange={setSearch} placeholder="Search clients…" testid="customers-search" />
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState
          title="No clients yet"
          description="Add your first client to start scheduling cleans."
          action={<Button onClick={openNew} className="rounded-sm">Add Client</Button>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState title="No matches" description={`No clients match “${search}”.`} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="label-caps">Name</TableHead>
              <TableHead className="label-caps">Phone</TableHead>
              <TableHead className="label-caps">Suburb</TableHead>
              <TableHead className="label-caps">Created</TableHead>
              <TableHead className="text-right label-caps">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell className="py-4">
                  <button type="button" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`customer-name-link-${c.id}`} className="font-medium text-left hover:text-primary hover:underline">{c.name}</button>
                  {c.business_name && <div className="text-xs text-brand" data-testid={`customer-business-${c.id}`}>{c.business_name}{c.abn ? ` · ABN ${c.abn}` : ""}</div>}
                  <div className="text-sm text-muted-foreground">{c.email}</div>
                </TableCell>
                <TableCell>{c.phone || "—"}{c.sms_opt_out && <div className="text-[10px] uppercase tracking-wider text-destructive" data-testid={`client-opted-out-${c.id}`}>SMS opted out</div>}
                  {issueFor(c.id) && <div className="text-[10px] uppercase tracking-wider text-destructive" data-testid={`client-unreachable-${c.id}`}>Can't be reminded</div>}</TableCell>
                <TableCell>{c.suburb || "—"}</TableCell>
                <TableCell className="text-muted-foreground" data-testid={`customer-created-${c.id}`}>
                  {c.created_at ? format(parseISO(c.created_at), "d MMM yyyy") : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/clients/${c.id}`)}
                      data-testid={`view-customer-${c.id}`}
                      className="rounded-sm gap-1.5 h-8"
                    >
                      <Eye className="w-3.5 h-3.5" /> View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/jobs?client=${c.id}`)}
                      data-testid={`book-customer-${c.id}`}
                      className="rounded-sm gap-1.5 h-8"
                    >
                      <CalendarPlus className="w-3.5 h-3.5" /> Book
                    </Button>
                    <button onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`} className="p-2 hover:text-primary text-muted-foreground">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(c.id)} data-testid={`delete-customer-${c.id}`} className="p-2 hover:text-destructive text-muted-foreground">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit Client" : "New Client"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Full name</Label>
              <Input value={form.name} onChange={set("name")} data-testid="customer-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Business name <span className="normal-case tracking-normal">(optional)</span></Label>
                <Input value={form.business_name || ""} onChange={set("business_name")} placeholder="e.g. Roo Holdings Pty Ltd" data-testid="customer-business-name" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">ABN <span className="normal-case tracking-normal">(optional)</span></Label>
                <Input value={form.abn || ""} onChange={set("abn")} placeholder="11 digits" inputMode="numeric" data-testid="customer-abn" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Email</Label>
                <Input value={form.email} onChange={set("email")} data-testid="customer-email" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Phone</Label>
                <Input value={form.phone} onChange={set("phone")} placeholder="0412 345 678" data-testid="customer-phone" />
                <p className="text-xs text-muted-foreground">Australian mobiles are saved as +61.</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Address</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                onSelect={(p) => setForm((f) => ({
                  ...f,
                  address: p.address || f.address,
                  suburb: p.suburb || f.suburb,
                  postcode: p.postcode || f.postcode,
                }))}
                testid="customer-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Suburb</Label>
                <Input value={form.suburb} onChange={set("suburb")} data-testid="customer-suburb" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Postcode</Label>
                <Input value={form.postcode} onChange={set("postcode")} data-testid="customer-postcode" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Reminder channel</Label>
              <select
                value={form.preferred_channel || "sms"}
                onChange={set("preferred_channel")}
                data-testid="customer-channel"
                className="w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand"
              >
                <option value="both">SMS &amp; Email</option>
                <option value="sms">SMS only</option>
                <option value="email">Email only</option>
              </select>
              <p className="text-xs text-muted-foreground">How this client is reminded before their booking.</p>
            </div>
            <div className="flex items-center justify-between border border-border/60 rounded-sm px-4 py-3">
              <div>
                <div className="text-sm font-medium">SMS opt-out</div>
                <p className="text-xs text-muted-foreground">Never text this client (also set automatically when they reply STOP).</p>
              </div>
              <Switch checked={!!form.sms_opt_out} onCheckedChange={(v) => setForm({ ...form, sms_opt_out: v })} data-testid="customer-sms-opt-out" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Notes</Label>
              <Textarea value={form.notes} onChange={set("notes")} data-testid="customer-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-customer-button" className="rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ImportCustomersDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
      <ClientIssuesDialog items={issues} open={issuesOpen} onOpenChange={setIssuesOpen}
        onFix={(id) => { const c = items.find((x) => x.id === id); if (c) openEdit(c); }} />
    </div>
  );
}
